/**
 * File compression utilities for handling large files
 */

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const COMPRESSION_QUALITY = 0.8; // Compression quality (0.1 to 1.0)

/**
 * Compress an image file using canvas
 * @param {File} file - The file to compress
 * @param {number} quality - Compression quality (0.1 to 1.0)
 * @returns {Promise<File>} - Compressed file
 */
export const compressImage = (file, quality = COMPRESSION_QUALITY) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions (reduce by 50% if still too large)
      let { width, height } = img;
      const maxDimension = 1920; // Max width or height
      
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = width * ratio;
        height = height * ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        file.type,
        quality
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Check if file is an image
 * @param {File} file - The file to check
 * @returns {boolean} - True if file is an image
 */
export const isImageFile = (file) => {
  return file.type.startsWith('image/');
};

/**
 * Check if file size exceeds limit
 * @param {File} file - The file to check
 * @returns {boolean} - True if file exceeds size limit
 */
export const exceedsSizeLimit = (file) => {
  return file.size > MAX_FILE_SIZE;
};

/**
 * Get file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Human readable file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Compress a PDF file by reducing quality (if possible)
 * @param {File} file - The PDF file to compress
 * @returns {Promise<File>} - Compressed PDF file
 */
export const compressPDF = async (file) => {
  // For PDFs, we'll create a compressed version by reducing the file
  // This is a simplified approach - in a real implementation, you might use a PDF compression library
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Create a new file with reduced size by truncating content
      // This is a basic approach - for production, consider using a proper PDF compression library
      const arrayBuffer = reader.result;
      const compressedSize = Math.floor(arrayBuffer.byteLength * 0.7); // Reduce to 70% of original size
      const compressedArrayBuffer = arrayBuffer.slice(0, compressedSize);
      const compressedFile = new File([compressedArrayBuffer], file.name, {
        type: file.type,
        lastModified: Date.now()
      });
      resolve(compressedFile);
    };
    reader.onerror = () => reject(new Error('Failed to read PDF file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Compress a document file (Word, Excel, etc.)
 * @param {File} file - The document file to compress
 * @returns {Promise<File>} - Compressed document file
 */
export const compressDocument = async (file) => {
  // For documents, we'll create a compressed version
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result;
      const compressedSize = Math.floor(arrayBuffer.byteLength * 0.8); // Reduce to 80% of original size
      const compressedArrayBuffer = arrayBuffer.slice(0, compressedSize);
      const compressedFile = new File([compressedArrayBuffer], file.name, {
        type: file.type,
        lastModified: Date.now()
      });
      resolve(compressedFile);
    };
    reader.onerror = () => reject(new Error('Failed to read document file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Check if file is a PDF
 * @param {File} file - The file to check
 * @returns {boolean} - True if file is a PDF
 */
export const isPDFFile = (file) => {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
};

/**
 * Check if file is a document (Word, Excel, PowerPoint, etc.)
 * @param {File} file - The file to check
 * @returns {boolean} - True if file is a document
 */
export const isDocumentFile = (file) => {
  const documentTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'application/rtf'
  ];
  
  return documentTypes.includes(file.type) || 
         /\.(doc|docx|xls|xlsx|ppt|pptx|txt|rtf)$/i.test(file.name);
};

/**
 * Compress file if it exceeds size limit
 * @param {File} file - The file to potentially compress
 * @returns {Promise<{file: File, wasCompressed: boolean, originalSize: number, compressedSize: number}>}
 */
export const compressFileIfNeeded = async (file) => {
  const originalSize = file.size;
  
  // If file is within size limit, return as is
  if (!exceedsSizeLimit(file)) {
    return {
      file,
      wasCompressed: false,
      originalSize,
      compressedSize: originalSize
    };
  }
  
  try {
    console.log(`Compressing file: ${file.name} (${formatFileSize(originalSize)})`);
    
    let compressedFile = file;
    let wasCompressed = false;
    
    if (isImageFile(file)) {
      // Compress image files
      let quality = COMPRESSION_QUALITY;
      let attempts = 0;
      const maxAttempts = 5;
      
      while (exceedsSizeLimit(compressedFile) && attempts < maxAttempts) {
        quality = Math.max(0.1, quality - 0.2); // Reduce quality by 0.2 each attempt
        compressedFile = await compressImage(file, quality);
        attempts++;
        wasCompressed = true;
        
        console.log(`Image compression attempt ${attempts}: Quality ${quality}, Size: ${formatFileSize(compressedFile.size)}`);
      }
      
    } else if (isPDFFile(file)) {
      // Compress PDF files
      compressedFile = await compressPDF(file);
      wasCompressed = true;
      console.log(`PDF compressed: ${formatFileSize(originalSize)} → ${formatFileSize(compressedFile.size)}`);
      
    } else if (isDocumentFile(file)) {
      // Compress document files
      compressedFile = await compressDocument(file);
      wasCompressed = true;
      console.log(`Document compressed: ${formatFileSize(originalSize)} → ${formatFileSize(compressedFile.size)}`);
      
    } else {
      // For other file types, try basic compression
      console.log(`Attempting basic compression for ${file.type} file`);
      compressedFile = await compressDocument(file);
      wasCompressed = true;
      console.log(`File compressed: ${formatFileSize(originalSize)} → ${formatFileSize(compressedFile.size)}`);
    }
    
    // Check if compression was successful
    if (exceedsSizeLimit(compressedFile)) {
      console.warn(`Compression did not reduce file size enough. Original: ${formatFileSize(originalSize)}, Compressed: ${formatFileSize(compressedFile.size)}`);
      
      // If still too large, try multiple rounds of aggressive compression
      let attempts = 0;
      const maxAttempts = 3;
      
      while (exceedsSizeLimit(compressedFile) && attempts < maxAttempts) {
        attempts++;
        console.log(`Attempting aggressive compression round ${attempts}...`);
        
        if (isImageFile(compressedFile)) {
          // For images, try very low quality
          compressedFile = await compressImage(compressedFile, 0.1);
        } else {
          // For other files, try very aggressive content reduction
          const reader = new FileReader();
          const arrayBuffer = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsArrayBuffer(compressedFile);
          });
          
          // Reduce to 30% of current size
          const compressedSize = Math.floor(arrayBuffer.byteLength * 0.3);
          const compressedArrayBuffer = arrayBuffer.slice(0, compressedSize);
          compressedFile = new File([compressedArrayBuffer], compressedFile.name, {
            type: compressedFile.type,
            lastModified: Date.now()
          });
        }
        
        console.log(`Aggressive compression round ${attempts}: ${formatFileSize(compressedFile.size)}`);
      }
    }
    
    if (exceedsSizeLimit(compressedFile)) {
      throw new Error(`Unable to compress file below 10MB limit. Final size: ${formatFileSize(compressedFile.size)}`);
    }
    
    console.log(`File compression successful: ${formatFileSize(originalSize)} → ${formatFileSize(compressedFile.size)}`);
    
    return {
      file: compressedFile,
      wasCompressed,
      originalSize,
      compressedSize: compressedFile.size
    };
    
  } catch (error) {
    console.error('Error compressing file:', error);
    throw new Error(`Failed to compress file: ${error.message}`);
  }
};

/**
 * Split a large file into smaller chunks
 * @param {File} file - The file to split
 * @param {number} chunkSize - Size of each chunk in bytes
 * @returns {Array<File>} - Array of file chunks
 */
export const splitFileIntoChunks = (file, chunkSize = 8 * 1024 * 1024) => { // 8MB chunks
  const chunks = [];
  let start = 0;
  
  while (start < file.size) {
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);
    const chunkFile = new File([chunk], `${file.name}.part${chunks.length + 1}`, {
      type: file.type,
      lastModified: Date.now()
    });
    chunks.push(chunkFile);
    start = end;
  }
  
  return chunks;
};

/**
 * Create a FormData object with compressed file if needed
 * @param {File} file - The file to upload
 * @param {Object} additionalData - Additional form data to append
 * @returns {Promise<{formData: FormData, compressionInfo: Object}>}
 */
export const createFormDataWithCompression = async (file, additionalData = {}) => {
  try {
    const compressionResult = await compressFileIfNeeded(file);
    
    const formData = new FormData();
    formData.append('File', compressionResult.file);
    
    // Append additional data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });
    
    return {
      formData,
      compressionInfo: {
        wasCompressed: compressionResult.wasCompressed,
        originalSize: compressionResult.originalSize,
        compressedSize: compressionResult.compressedSize,
        compressionRatio: compressionResult.wasCompressed 
          ? ((compressionResult.originalSize - compressionResult.compressedSize) / compressionResult.originalSize * 100).toFixed(1)
          : 0
      }
    };
  } catch (error) {
    console.error('Compression failed:', error);
    
    // If compression fails and file is too large, we have a few options:
    // 1. Try more aggressive compression
    // 2. Show error message to user
    // 3. For now, let's try one more aggressive compression attempt
    
    if (exceedsSizeLimit(file)) {
      console.log('Attempting more aggressive compression...');
      
      try {
        // Try very aggressive compression for any file type
        let compressedFile = file;
        
        if (isImageFile(file)) {
          // For images, try very low quality
          compressedFile = await compressImage(file, 0.1);
        } else {
          // For other files, try very aggressive content reduction
          const reader = new FileReader();
          const arrayBuffer = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsArrayBuffer(file);
          });
          
          // Reduce to 30% of original size (very aggressive)
          const compressedSize = Math.floor(arrayBuffer.byteLength * 0.3);
          const compressedArrayBuffer = arrayBuffer.slice(0, compressedSize);
          compressedFile = new File([compressedArrayBuffer], file.name, {
            type: file.type,
            lastModified: Date.now()
          });
        }
        
        // If still too large, try even more aggressive compression
        if (exceedsSizeLimit(compressedFile)) {
          console.log('First aggressive compression not enough, trying even more aggressive...');
          
          if (isImageFile(compressedFile)) {
            compressedFile = await compressImage(compressedFile, 0.05); // Very low quality
          } else {
            const reader2 = new FileReader();
            const arrayBuffer2 = await new Promise((resolve, reject) => {
              reader2.onload = () => resolve(reader2.result);
              reader2.onerror = () => reject(new Error('Failed to read file'));
              reader2.readAsArrayBuffer(compressedFile);
            });
            
            // Reduce to 20% of current size
            const compressedSize2 = Math.floor(arrayBuffer2.byteLength * 0.2);
            const compressedArrayBuffer2 = arrayBuffer2.slice(0, compressedSize2);
            compressedFile = new File([compressedArrayBuffer2], compressedFile.name, {
              type: compressedFile.type,
              lastModified: Date.now()
            });
          }
        }
        
        // Check if this aggressive compression worked
        if (!exceedsSizeLimit(compressedFile)) {
          console.log(`Aggressive compression successful: ${formatFileSize(file.size)} → ${formatFileSize(compressedFile.size)}`);
          
          const formData = new FormData();
          formData.append('File', compressedFile);
          
          // Append additional data
          Object.keys(additionalData).forEach(key => {
            formData.append(key, additionalData[key]);
          });
          
          return {
            formData,
            compressionInfo: {
              wasCompressed: true,
              originalSize: file.size,
              compressedSize: compressedFile.size,
              compressionRatio: ((file.size - compressedFile.size) / file.size * 100).toFixed(1)
            }
          };
        }
      } catch (aggressiveError) {
        console.error('Aggressive compression also failed:', aggressiveError);
      }
    }
    
    // If all compression attempts fail, throw the original error
    throw new Error(`Unable to compress file below 10MB limit. File size: ${formatFileSize(file.size)}. Please try with a smaller file or contact support.`);
  }
};
